import { useState } from 'react';
import { X, Save, Plus, Trash2, PlugZap } from 'lucide-react';
import { testAgentMcpServer, type McpServerTestResult } from '../api/agents';
import type { McpServerTransport } from '../api/agents';
import { getApiErrorMessage } from './configcenter/utils';

export interface McpServer {
  id: string;
  name: string;
  description?: string;
  command?: string | null;
  args?: string[];
  env?: Record<string, string>;
  /** stdio（默认）/ http / sse */
  transport?: McpServerTransport;
  url?: string | null;
  /** 远程条目自定义请求头（明文） */
  headers?: Record<string, string>;
  /** Bearer token（write-only：只提交不回显；空串=清除） */
  token?: string;
  /** 是否已配置 token（后端不回传值；表单留空表示不修改） */
  has_token?: boolean;
}

/** transport 中文标签（单选用）。 */
const TRANSPORT_OPTIONS: Array<{ value: McpServerTransport; label: string }> = [
  { value: 'stdio', label: '本地命令（stdio）' },
  { value: 'http', label: '远程 HTTP' },
  { value: 'sse', label: '远程 SSE' },
];

function isRemote(transport: McpServerTransport): boolean {
  return transport === 'http' || transport === 'sse';
}

interface McpServerEditorProps {
  server?: McpServer | null;
  /** 归属 Agent ID：传入且编辑已入库 server 时显示「测试连接」按钮 */
  agentId?: string;
  onSave: (server: McpServer) => void;
  onCancel: () => void;
}

/** 连通性测试状态的中文标签。 */
const TEST_STATUS_LABEL: Record<McpServerTestResult['status'], string> = {
  passed: '通过',
  failed: '未通过',
  degraded: '降级',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  width: '90%',
  maxWidth: 560,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 24,
};

export default function McpServerEditor({ server, agentId, onSave, onCancel }: McpServerEditorProps) {
  const isEdit = !!server;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [command, setCommand] = useState('');
  const [args, setArgs] = useState<string[]>([]);
  const [env, setEnv] = useState<Record<string, string>>({});
  // 远程 transport（P3-②）：URL + headers + token（token write-only，只发不回）
  const [transport, setTransport] = useState<McpServerTransport>('stdio');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [token, setToken] = useState('');
  const remote = isRemote(transport);

  // 连通性测试状态（仅编辑已入库 server 时可用）
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<McpServerTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const canTest = !!agentId && !!server?.id;

  // 渲染期调整：server prop 变化时同步表单（替代原 useEffect）
  const [prevServer, setPrevServer] = useState(server);
  if (prevServer !== server) {
    setPrevServer(server);
    if (server) {
      setName(server.name);
      setDescription(server.description || '');
      setCommand(server.command || '');
      setArgs(server.args || []);
      setEnv(server.env || {});
      setTransport(server.transport || 'stdio');
      setUrl(server.url || '');
      setHeaders({ ...(server.headers as Record<string, string> | undefined) });
      // token 不回传：编辑时永远留空（留空=不修改；需清除请提交空串由上层处理）
      setToken('');
    } else {
      setName('');
      setDescription('');
      setCommand('');
      setArgs([]);
      setEnv({});
      setTransport('stdio');
      setUrl('');
      setHeaders({});
      setToken('');
    }
  }

  const handleAddArg = () => setArgs([...args, '']);
  const handleRemoveArg = (idx: number) => {
    const next = [...args];
    next.splice(idx, 1);
    setArgs(next);
  };
  const handleUpdateArg = (idx: number, value: string) => {
    const next = [...args];
    next[idx] = value;
    setArgs(next);
  };

  const envEntries = Object.entries(env);
  const handleAddEnv = () => setEnv({ ...env, '': '' });
  const handleRemoveEnv = (key: string) => {
    const next = { ...env };
    delete next[key];
    setEnv(next);
  };
  const handleUpdateEnv = (oldKey: string, newKey: string, value: string) => {
    const next: Record<string, string> = {};
    Object.entries(env).forEach(([k, v]) => {
      if (k === oldKey) {
        next[newKey] = value;
      } else {
        next[k] = v;
      }
    });
    setEnv(next);
  };

  const headerEntries = Object.entries(headers);
  const handleAddHeader = () => setHeaders({ ...headers, '': '' });
  const handleRemoveHeader = (key: string) => {
    const next = { ...headers };
    delete next[key];
    setHeaders(next);
  };
  const handleUpdateHeader = (oldKey: string, newKey: string, value: string) => {
    const next: Record<string, string> = {};
    Object.entries(headers).forEach(([k, v]) => {
      if (k === oldKey) {
        next[newKey] = value;
      } else {
        next[k] = v;
      }
    });
    setHeaders(next);
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    if (remote) {
      // 远程条目：URL 必填；token 仅在填写时随 payload 提交（write-only，空=不修改）
      if (!url.trim()) return;
      const cleanHeaders: Record<string, string> = {};
      headerEntries.forEach(([k, v]) => {
        if (k.trim()) cleanHeaders[k.trim()] = v;
      });
      onSave({
        id: server?.id || `mcp-${Date.now()}`,
        name: name.trim(),
        description: description.trim(),
        transport,
        url: url.trim(),
        headers: cleanHeaders,
        ...(token.trim() ? { token: token.trim() } : {}),
      });
      return;
    }
    if (!command.trim()) return;
    onSave({
      id: server?.id || `mcp-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      command: command.trim(),
      args: args.filter((a) => a.trim()),
      env,
    });
  };

  const handleTestConnection = async () => {
    if (!agentId || !server?.id) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await testAgentMcpServer(agentId, server.id);
      setTestResult(result);
    } catch (e) {
      setTestError(getApiErrorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ margin: 0 }}>{isEdit ? '编辑 MCP 工具' : '新增 MCP 工具'}</h3>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onCancel}>
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>名称</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="如：文件系统 MCP" />
          </div>
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="用途描述"
              rows={2}
            />
          </div>
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>类型</label>
            <div className="flex gap-2">
              {TRANSPORT_OPTIONS.map((opt) => (
                <label key={opt.value} className="text-sm flex items-center gap-1">
                  <input
                    type="radio"
                    name="mcp-transport"
                    value={opt.value}
                    checked={transport === opt.value}
                    onChange={() => setTransport(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          {!remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>启动命令</label>
              <input value={command} onChange={(e) => setCommand(e.target.value)} placeholder="如：npx / uvx / docker" />
            </div>
          )}

          {remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                服务地址（URL）
              </label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://mcp.example.com/mcp（仅公网 http/https，禁内网地址）"
              />
            </div>
          )}

          {remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>请求头</label>
              {headerEntries.map(([key, value]) => (
                <div key={key} className="flex gap-2 mb-2">
                  <input
                    value={key}
                    onChange={(e) => handleUpdateHeader(key, e.target.value, value)}
                    placeholder="头名（如 X-Tenant）"
                  />
                  <input
                    value={value}
                    onChange={(e) => handleUpdateHeader(key, key, e.target.value)}
                    placeholder="头值"
                  />
                  <button className="danger" style={{ padding: '6px 10px' }} onClick={() => handleRemoveHeader(key)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button className="secondary" style={{ padding: '6px 10px', fontSize: 12 }} onClick={handleAddHeader}>
                <Plus size={12} /> 添加请求头
              </button>
            </div>
          )}

          {remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                访问令牌（Bearer Token，可选）
              </label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={server?.has_token ? '已配置，留空则不修改' : '未配置'}
              />
            </div>
          )}

          {!remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>参数</label>
              {args.map((arg, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input value={arg} onChange={(e) => handleUpdateArg(idx, e.target.value)} placeholder={`参数 ${idx + 1}`} />
                  <button className="danger" style={{ padding: '6px 10px' }} onClick={() => handleRemoveArg(idx)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button className="secondary" style={{ padding: '6px 10px', fontSize: 12 }} onClick={handleAddArg}>
                <Plus size={12} /> 添加参数
              </button>
            </div>
          )}

          {!remote && (
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>环境变量</label>
              {envEntries.map(([key, value]) => (
                <div key={key} className="flex gap-2 mb-2">
                  <input
                    value={key}
                    onChange={(e) => handleUpdateEnv(key, e.target.value, value)}
                    placeholder="变量名"
                  />
                  <input
                    value={value}
                    onChange={(e) => handleUpdateEnv(key, key, e.target.value)}
                    placeholder="变量值"
                  />
                  <button className="danger" style={{ padding: '6px 10px' }} onClick={() => handleRemoveEnv(key)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button className="secondary" style={{ padding: '6px 10px', fontSize: 12 }} onClick={handleAddEnv}>
                <Plus size={12} /> 添加环境变量
              </button>
            </div>
          )}
        </div>

        {(testResult || testError) && (
          <div
            data-testid="mcp-test-result"
            className="text-sm"
            style={{
              marginTop: 16,
              padding: 12,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-primary)',
            }}
          >
            {testError && <div style={{ color: 'var(--danger, #e5534b)' }}>测试失败：{testError}</div>}
            {testResult && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-muted">状态：</span>
                  <span
                    style={{
                      color:
                        testResult.status === 'passed'
                          ? 'var(--success, #3fb950)'
                          : testResult.status === 'failed'
                            ? 'var(--danger, #e5534b)'
                            : 'var(--warning, #d29922)',
                    }}
                  >
                    {TEST_STATUS_LABEL[testResult.status]}
                  </span>
                  <span className="text-muted">耗时 {testResult.duration_ms} ms</span>
                </div>
                <div className="text-muted" style={{ marginTop: 4 }}>
                  工具（{testResult.tool_count}）：
                  {testResult.tools.length > 0 ? testResult.tools.map((t) => t.name).join('、') : '无'}
                </div>
                {testResult.detail && <div style={{ marginTop: 4 }}>{testResult.detail}</div>}
                {testResult.static_issues.length > 0 && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {testResult.static_issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                )}
                {testResult.suggestions.length > 0 && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {testResult.suggestions.map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          {canTest && (
            <button className="secondary" onClick={handleTestConnection} disabled={testing}>
              <PlugZap size={14} /> {testing ? '测试中…' : '测试连接'}
            </button>
          )}
          <button className="primary" onClick={handleSubmit} style={{ flex: 1 }}>
            <Save size={14} /> 保存
          </button>
          <button className="secondary" onClick={onCancel}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
